/**
 * /admin/matches — Coded matches by user (folder-per-user view).
 *
 * Founder ask 2026-06-23: only matches actually coded by users (not
 * ingested archive data), grouped per user. Default view = list of
 * user folders with match counts + last-match-date; click a folder
 * to drill into that user's matches with Excel download buttons.
 *
 * Backend:
 *   GET /api/admin/v2/matches/owners/   (folder list)
 *   GET /api/admin/v2/matches/all/?owner_email=&format=&date_from=&date_to=
 *       (matches inside a folder — restricted to owner_type=USER)
 *   GET /api/admin/v2/matches/<id>/export-xlsx/   (per-row download)
 *
 * Future matches: backend filters by owner_type="USER" + innings_count > 0
 * — any newly coded match auto-appears in the right folder; no
 * backfill / sync needed.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  Search, RefreshCw, RefreshCcw, FileSpreadsheet, Trophy, Folder,
  ArrowLeft, Mail, ClipboardList, ShieldCheck, Trash2, X,
} from "lucide-react";
import {
  listAdminAllMatches, listAdminMatchOwners, downloadAdminMatchXlsx,
  deleteAdminMatch,
} from "../../api/owner.api";
import api from "../../api/axios";
import { groupByTournament, seasonSpan } from "../../utils/tournamentGroups";

// Fallback used until /api/matches/matchtypes/ resolves. Keeps the
// dropdown usable on a cold cache. Replaced on first successful fetch
// with every active MatchType (T10/T15/T20/T20_BBL/T30/ODI/TEST/HUNDRED/…)
// so the filter is never out of sync with what the match-creation page
// offers.
const FORMATS_FALLBACK = [
  { key: "",        label: "All formats" },
  { key: "T20",     label: "T20" },
  { key: "T20_BBL", label: "T20 (BBL)" },
  { key: "ODI",     label: "ODI" },
  { key: "TEST",    label: "Test" },
  { key: "HUNDRED", label: "The Hundred" },
  { key: "T10",     label: "T10" },
];

// Status badge styling per lifecycle (see AdminMatchesByOwnerView).
// ACTIVE → no badge (clean folder); the others all render an inline
// pill so the founder can tell at a glance which folders represent
// deleted / suspended owners without opening them.
function LifecycleBadge({ lifecycle }) {
  if (!lifecycle || lifecycle === "ACTIVE") return null;
  const styles = {
    STANDBY:       "bg-amber-500/15  border-amber-500/40  text-amber-300",
    SUSPENDED:     "bg-orange-500/15 border-orange-500/40 text-orange-300",
    DELETED_ADMIN: "bg-red-500/15    border-red-500/40    text-red-300",
    DELETED_SELF:  "bg-zinc-500/20   border-zinc-500/40   text-zinc-300",
    ORPHAN:        "bg-zinc-500/20   border-zinc-500/40   text-zinc-300",
  };
  const label = {
    STANDBY:       "Standby",
    SUSPENDED:     "Suspended",
    DELETED_ADMIN: "Deleted",
    DELETED_SELF:  "Deleted",
    ORPHAN:        "Deleted (legacy)",
  }[lifecycle] || lifecycle;
  return (
    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[lifecycle] || ""} ml-2`}>
      {label}
    </span>
  );
}

// ── Folder list (default view) ───────────────────────────────────────
function FolderList({
  ownerSearch, setOwnerSearch,
  competitionOptions, onPickCompetition,
  formatOptions, onPickFormat,
  onOpen,
}) {
  const [owners, setOwners]   = useState([]);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(true);
  // 2026-07-06 — coded / not-coded filter. Segmented pill above the
  // grid so the founder can carve the (long) folder list into "who has
  // actually scored balls" vs "who signed up or created drafts but
  // never coded". Backend supports coded_filter="has_coded"/"no_coded".
  const [codedFilter, setCodedFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await listAdminMatchOwners({
          search: ownerSearch || undefined,
          coded_filter: codedFilter || undefined,
        });
        setOwners(data.results || []);
        setCount(data.count || 0);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [ownerSearch, codedFilter]);

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-5 h-5 text-text-secondary" />
        <h1 className="text-lg font-semibold">Coded Matches · by User</h1>
        <span className="text-xs text-text-secondary font-mono">
          {loading ? "…" : `${count.toLocaleString("en-IN")} users`}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-text-secondary" />
          <input
            type="text"
            value={ownerSearch}
            onChange={(e) => setOwnerSearch(e.target.value)}
            placeholder="Filter users by email / name…"
            className="w-full md:w-80 bg-bg-secondary text-text-primary pl-7 pr-2 py-1.5 rounded text-xs"
          />
        </div>
        {/* 2026-07-06 — coded / not-coded segmented filter. "Coded" =
            the user has scored at least one ball on a match (i.e.
            coded_match_count > 0). "Not coded" = signed up / created
            drafts but hasn't scored. */}
        <div className="inline-flex items-center rounded-md border border-border-default bg-bg-secondary p-0.5">
          {[
            { k: "",          l: "All users" },
            { k: "has_coded", l: "Coded matches" },
            { k: "no_coded",  l: "No coded matches" },
          ].map((opt) => (
            <button
              key={opt.k || "all"}
              type="button"
              onClick={() => setCodedFilter(opt.k)}
              className={`px-3 py-1 rounded transition-colors ${
                codedFilter === opt.k
                  ? "bg-button-primary text-white"
                  : "text-text-secondary hover:text-text-primary"
              }`}
              title={
                opt.k === "has_coded"
                  ? "Only users who have scored balls on at least one match"
                  : opt.k === "no_coded"
                    ? "Only users who signed up or created drafts but haven't coded a match yet"
                    : "Every user"
              }
            >
              {opt.l}
            </button>
          ))}
        </div>
        {/* Cross-user shortcut: pick a competition and skip the
            folder grid entirely. Backend AdminAllMatchesListView
            already returns every USER-coded match for the chosen
            competition_id across all users, so each row will show
            the coder inline. The picker shows one row per logical
            league (server merges "IPL 2026" / "Indian Premier League"
            / "Indian Premier League 2026" → one entry). */}
        <select
          value=""
          onChange={(e) => {
            const id = e.target.value;
            if (id) onPickCompetition(id);
          }}
          title="Jump to all matches coded across users for one competition"
          className="bg-bg-secondary text-text-primary px-2 py-1.5 rounded"
        >
          <option value="">Browse by competition…</option>
          {competitionOptions.map((c) => {
            // Coder + variant hints: small parenthetical so a quick
            // glance tells the founder both "how many users have a
            // match here" and "how many duplicate Competition rows fed
            // this one entry."
            const hints = [];
            if (c.coder_count >= 1) {
              hints.push(`${c.coder_count} coder${c.coder_count === 1 ? "" : "s"}`);
            }
            if (c.variant_count > 1) {
              hints.push(`${c.variant_count} variants`);
            }
            return (
              <option key={c.id} value={c.id}>
                {c.name}{c.season ? ` (${c.season})` : ""} · {c.match_count}
                {hints.length ? ` (${hints.join(", ")})` : ""}
              </option>
            );
          })}
        </select>
        {/* Format-only shortcut. Picking T20 / ODI / Test jumps to a
            cross-user list of every match coded in that format,
            regardless of competition. */}
        <select
          value=""
          onChange={(e) => {
            const code = e.target.value;
            if (code) onPickFormat(code);
          }}
          title="Jump to all matches coded across users in one format"
          className="bg-bg-secondary text-text-primary px-2 py-1.5 rounded"
        >
          <option value="">Browse by format…</option>
          {formatOptions
            .filter((f) => f.key)
            .map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
        </select>
      </div>

      {loading && (
        <div className="text-xs text-text-secondary flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}

      {!loading && owners.length === 0 && (
        <div className="text-xs text-text-secondary py-6 text-center bg-bg-secondary/30 rounded border border-bg-secondary">
          No users have coded any matches yet.
        </div>
      )}

      {!loading && owners.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {owners.map((o) => (
            <button
              key={o.user_id}
              onClick={() => onOpen(o)}
              className="text-left flex items-start gap-3 rounded border border-border-default bg-bg-secondary/40 hover:bg-bg-secondary/60 px-3 py-2.5 transition-colors"
              title={`Open ${o.email}'s matches`}
            >
              <Folder className={`w-5 h-5 shrink-0 mt-0.5 ${
                o.lifecycle === "DELETED_SELF" || o.lifecycle === "DELETED_ADMIN" || o.lifecycle === "ORPHAN"
                  ? "text-zinc-500"
                  : o.lifecycle === "SUSPENDED"
                    ? "text-orange-400"
                    : "text-amber-300"
              }`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center">
                  <span className="truncate">{o.full_name}</span>
                  <LifecycleBadge lifecycle={o.lifecycle} />
                </div>
                <div className="text-[10px] text-text-secondary truncate flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {o.email || <span className="italic">email unavailable</span>}
                </div>
                <div className="text-[10px] text-text-secondary mt-0.5">
                  <span className="font-mono text-text-primary">
                    {o.match_count.toLocaleString("en-IN")}
                  </span>{" "}
                  match{o.match_count === 1 ? "" : "es"}
                  {/* 2026-07-06 — surface coded_match_count so the
                      founder can tell "created but not coded" folders
                      from "actually scored" folders at a glance. */}
                  {typeof o.coded_match_count === "number" && (
                    <span
                      className={
                        o.coded_match_count > 0
                          ? "ml-1 text-emerald-400"
                          : "ml-1 text-text-secondary"
                      }
                      title={
                        o.coded_match_count > 0
                          ? "Coded matches — user has scored balls on these"
                          : "No matches coded yet"
                      }
                    >
                      · {o.coded_match_count.toLocaleString("en-IN")} coded
                    </span>
                  )}
                  {o.last_match_date && (
                    <span> · latest {o.last_match_date}</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ── Match list inside a folder OR scoped to one competition ──────────
// Pass exactly one of:
//   owner       — drill-in from the folder grid (per-user view)
//   competition — drill-in from the "Browse by competition" picker
//                 (cross-user flat list; coder rendered on each row)
// `initialFormat` pre-seeds the inner format filter (used when the
// folder-level "Browse by format" picker triggered the scope view).
function MatchList({
  owner,
  competition: scopeCompetition,
  initialFormat = "",
  competitionOptions: initialCompetitionOptions,
  onBack,
}) {
  const competitionMode = !owner && !!scopeCompetition;
  // crossUserMode covers competition-mode OR format-only scope. Owner
  // mode is the only case where we DON'T want to surface the coder
  // per row + the "all coders" badge.
  const crossUserMode = !owner;
  const [search, setSearch]     = useState("");
  const [format, setFormat]     = useState(initialFormat || "");
  const [competition, setCompetition] = useState(
    competitionMode ? scopeCompetition.id : "",
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [page, setPage]         = useState(1);
  const [rows, setRows]         = useState([]);
  const [count, setCount]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  // Competition dropdown is fed once on mount from /admin/v2/competitions/
  // — the endpoint only returns competitions with at least one coded
  // match so we never offer an option that yields an empty result set.
  // In competition-mode the parent has already fetched + passed them in,
  // so we seed from that to avoid a second round-trip.
  const [competitionOptions, setCompetitionOptions] = useState(
    initialCompetitionOptions || [],
  );

  // Format dropdown fed from /api/matches/matchtypes/ so the filter
  // matches every active MatchType — same source the match-creation
  // form uses, so the list can never drift. Falls back to a frozen
  // shortlist while the request is in flight or if it errors.
  const [formatOptions, setFormatOptions] = useState(FORMATS_FALLBACK);
  useEffect(() => {
    let alive = true;
    api.get("/api/matches/matchtypes/")
      .then((res) => {
        if (!alive) return;
        const data = res.data?.results || res.data || [];
        if (!Array.isArray(data) || data.length === 0) return;
        setFormatOptions([
          { key: "", label: "All formats" },
          ...data.map((r) => ({
            key: (r.code || r.name || "").toUpperCase(),
            label: r.name || r.code,
          })),
        ]);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Refresh from prop in case parent's fetch finished after mount.
    setCompetitionOptions(initialCompetitionOptions || []);
  }, [initialCompetitionOptions]);

  // SYSTEM/Ingested folder needs its own competition list — the parent's
  // fetch is USER-scoped (drives the FolderList "Browse by competition"
  // picker) and would leave this dropdown empty for the ingested archive.
  // Re-fetch with owner_scope=SYSTEM so IPL 2019 / IPL 2020 / TNPL 2024
  // show up here without changing the parent's list.
  const isSystemFolder = owner?.owner_scope === "SYSTEM";
  useEffect(() => {
    if (!isSystemFolder) return;
    let alive = true;
    api.get("/api/admin/v2/competitions/", {
      params: { owner_scope: "SYSTEM" },
    })
      .then((res) => {
        if (!alive) return;
        setCompetitionOptions(res.data?.results || []);
      })
      .catch(() => { /* keep whatever we had */ });
    return () => { alive = false; };
  }, [isSystemFolder]);

  // Seasons | Tournaments. The ingested archive holds one competition
  // per season; the tournament view rolls every season of IPL / TNPL /
  // … into one option, so the list and Sync cover the whole tournament.
  const [compView, setCompView] = useState("season");
  const tournamentView = isSystemFolder && compView === "tournament";
  const tournamentOptions = useMemo(
    () => (isSystemFolder ? groupByTournament(competitionOptions) : []),
    [isSystemFolder, competitionOptions],
  );
  const pickedTournament = tournamentView
    ? tournamentOptions.find((t) => t.id === competition)
    : null;
  const switchCompView = (v) => {
    if (v === compView) return;
    setCompView(v);
    setCompetition("");
  };

  useEffect(() => {
    const t = setTimeout(() => fetchPage(1), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, format, competition, dateFrom, dateTo, owner?.email, owner?.user_id]);

  useEffect(() => {
    fetchPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function fetchPage(p) {
    setLoading(true);
    try {
      // Owner-mode: prefer email when we have it (handles renamed/
      // soft-deleted users by their preserved email). Fall back to
      // user_id for orphan folders where the legacy hard-delete wiped
      // the User row. Competition-mode: no owner scoping — backend
      // returns every USER-coded match in the competition.
      let scopeParams = {};
      if (owner) {
        scopeParams = owner.email
          ? { owner_email: owner.email }
          : owner.user_id
            ? { owner_user_id: owner.user_id }
            : {};
      }
      const { data } = await listAdminAllMatches({
        ...scopeParams,
        search:        search || undefined,
        // `format` is a DRF-reserved content-negotiation key
        // (?format=json), so we send our format filter under
        // `match_format` to avoid 404ing the endpoint. Backend
        // accepts either; this is the canonical one.
        match_format:  format || undefined,
        competition:   competition || undefined,
        date_from:     dateFrom || undefined,
        date_to:       dateTo || undefined,
        page: p,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
      if (p === 1 && page !== 1) setPage(1);
      // 2026-08-04 — probe which of these matches have an uploaded
      // full-match video and its Glacier state, so each row can show
      // Retrieve / Retrieving / Video-ready next to Tag. One batch
      // call per page; failure is silent (chips just don't render).
      const ids = (data.results || []).map((m) => m.id);
      if (ids.length) {
        api.post("/api/admin/v2/matches/video-archive-states/", { match_ids: ids })
          .then((r) => setVideoStates((prev) => ({ ...prev, ...(r.data?.states || {}) })))
          .catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }

  // {<match_id>: {present, state, restore_expiry}} from the batch probe.
  const [videoStates, setVideoStates] = useState({});
  const [retrievingId, setRetrievingId] = useState(null);

  async function handleRetrieve(matchId) {
    if (retrievingId) return;
    setRetrievingId(matchId);
    try {
      const r = await api.post(`/api/admin/v2/matches/${matchId}/retrieve-whole-match-video/`);
      setVideoStates((prev) => ({
        ...prev,
        [matchId]: {
          present: true,
          state: r.data?.state || "retrieving",
          restore_expiry: r.data?.restore_expiry || null,
        },
      }));
    } catch (e) {
      window.alert(
        "Retrieve failed: " + (e?.response?.data?.detail || e?.message || "unknown error"),
      );
    } finally {
      setRetrievingId(null);
    }
  }

  async function handleDownload(matchId) {
    if (downloadingId) return;
    setDownloadingId(matchId);
    try {
      await downloadAdminMatchXlsx(matchId);
    } catch (e) {
      window.alert(
        "Download failed: " +
        (e?.response?.data?.detail || e?.message || "unknown error"),
      );
    } finally {
      setDownloadingId(null);
    }
  }

  // Delete-confirm modal state. `pendingDelete` holds the row the operator
  // clicked; null means modal is closed. A two-step confirm (modal +
  // "Delete" button) is enough — no typing the team name. Founder asked
  // for a simple per-row action, not a SQL safety harness.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  async function confirmDelete() {
    if (!pendingDelete || deletingId) return;
    const id = pendingDelete.id;
    setDeletingId(id);
    try {
      await deleteAdminMatch(id);
      setPendingDelete(null);
      // Drop the row from local state so the table updates instantly,
      // and decrement the count so the header doesn't lie until the
      // next refetch.
      setRows((r) => r.filter((m) => m.id !== id));
      setCount((c) => Math.max(0, c - 1));
    } catch (e) {
      window.alert(
        "Delete failed: " +
        (e?.response?.data?.detail || e?.message || "unknown error"),
      );
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-text-primary"
          title="Back to user list"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All users
        </button>
        <span className="text-text-secondary">·</span>
        {competitionMode ? (
          <>
            <Trophy className="w-5 h-5 text-amber-300" />
            <h1 className="text-lg font-semibold truncate">
              {scopeCompetition.name}
              {scopeCompetition.season && (
                <span className="text-text-secondary font-normal ml-1">
                  ({scopeCompetition.season})
                </span>
              )}
            </h1>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/15 text-amber-300 ml-2">
              All coders
            </span>
            {initialFormat && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/15 text-sky-300 ml-1">
                {initialFormat}
              </span>
            )}
          </>
        ) : crossUserMode ? (
          <>
            <Trophy className="w-5 h-5 text-sky-300" />
            <h1 className="text-lg font-semibold truncate">
              All matches
              {initialFormat && (
                <span className="text-text-secondary font-normal ml-1">
                  · {initialFormat}
                </span>
              )}
            </h1>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-500/40 bg-sky-500/15 text-sky-300 ml-2">
              All coders
            </span>
          </>
        ) : (
          <>
            <Folder className={`w-5 h-5 ${
              owner.lifecycle === "DELETED_SELF" || owner.lifecycle === "DELETED_ADMIN" || owner.lifecycle === "ORPHAN"
                ? "text-zinc-500"
                : owner.lifecycle === "SUSPENDED"
                  ? "text-orange-400"
                  : "text-amber-300"
            }`} />
            <h1 className="text-lg font-semibold truncate">{owner.full_name}</h1>
            <LifecycleBadge lifecycle={owner.lifecycle} />
            <span className="text-xs text-text-secondary truncate hidden md:inline">
              ({owner.email || "email unavailable"})
            </span>
          </>
        )}
        <span className="text-xs text-text-secondary font-mono ml-auto">
          {loading ? "…" : `${count.toLocaleString("en-IN")} match${count === 1 ? "" : "es"}`}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3 text-xs">
        {/* The ingested folder's competition cell also carries the
            Seasons | Tournaments toggle and Sync, which don't fit one
            column — it takes one from the search box there. */}
        <div className={`${isSystemFolder ? "md:col-span-1" : "md:col-span-2"} relative`}>
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-text-secondary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Team / venue / title…"
            className="w-full bg-bg-secondary text-text-primary pl-7 pr-2 py-1.5 rounded"
          />
        </div>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="bg-bg-secondary text-text-primary px-2 py-1.5 rounded"
        >
          {formatOptions.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        {/* In competition-mode the page is already scoped to one
            competition; hide the picker to avoid scope-switching
            mid-view. Back button returns to the folder grid where
            "Browse by competition" lives. */}
        {!competitionMode && (
          <div className={`flex items-center gap-1 min-w-0 ${isSystemFolder ? "md:col-span-2" : ""}`}>
            {isSystemFolder && (
              <div
                className="shrink-0 inline-flex rounded border border-border-default overflow-hidden text-[10px] uppercase tracking-wider"
                role="group"
                aria-label="List competitions by season or by tournament"
              >
                {[
                  { id: "season", label: "Seasons" },
                  { id: "tournament", label: "Tournaments" },
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => switchCompView(o.id)}
                    aria-pressed={compView === o.id}
                    className={`px-2 py-1.5 ${
                      compView === o.id
                        ? "bg-button-primary/20 text-button-primary"
                        : "bg-bg-secondary text-text-secondary hover:text-text-primary"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            <select
              value={competition}
              onChange={(e) => setCompetition(e.target.value)}
              title="Filter by competition / tournament"
              className="flex-1 bg-bg-secondary text-text-primary px-2 py-1.5 rounded min-w-[6rem]"
            >
              <option value="">
                {tournamentView ? "All tournaments" : "All competitions"}
              </option>
              {tournamentView && tournamentOptions.map((t) => (
                <option key={t.key} value={t.id}>
                  {`${t.name} · ${t.seasons.length} season${
                    t.seasons.length === 1 ? "" : "s"
                  } ${seasonSpan(t.seasons)} (${t.match_count})`}
                </option>
              ))}
              {!tournamentView && competitionOptions.map((c) => {
                // Format: "IPL 2019 (60)". Add the season only when
                // it's not already inline in the name (SYSTEM-ingested
                // Competitions like "IPL 2019" already carry the year).
                const nameHasSeason =
                  c.season && String(c.name || "").includes(String(c.season));
                const label = c.season && !nameHasSeason
                  ? `${c.name} ${c.season} (${c.match_count})`
                  : `${c.name} (${c.match_count})`;
                return (
                  <option key={c.id} value={c.id}>
                    {label}
                  </option>
                );
              })}
            </select>
            {/* Sync tournament — opens the competition-scope sync page
                (all matches in this tournament in one shot). Backend
                collapses variant Competition rows into one id string
                (comma-joined); take the first uuid so the sync page
                gets a single scope. Rare edge case; the FIRST id owns
                the biggest share of matches in that group. */}
            {competition && (
              <Link
                to={
                  pickedTournament
                    ? `/cricket/competitions/tournament-sync?${new URLSearchParams({
                        ids: pickedTournament.id,
                        name: pickedTournament.name,
                      })}`
                    : `/cricket/competitions/${competition.split(",")[0]}/sync`
                }
                title={
                  pickedTournament
                    ? `Sync every ${pickedTournament.name} season at once — each player is mapped once`
                    : "Sync all matches in this tournament"
                }
                className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-1.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25 inline-flex items-center gap-1"
              >
                <RefreshCcw className="w-3 h-3" /> Sync
              </Link>
            )}
          </div>
        )}
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          title="From date"
          className="bg-bg-secondary text-text-primary px-2 py-1.5 rounded"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          title="To date"
          className="bg-bg-secondary text-text-primary px-2 py-1.5 rounded"
        />
      </div>

      {loading && (
        <div className="text-xs text-text-secondary flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-xs text-text-secondary py-6 text-center bg-bg-secondary/30 rounded border border-bg-secondary">
          No matches match these filters.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-1">
          {rows.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 rounded border border-border-default bg-bg-secondary/40 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {m.match_number != null && (
                    <span className="text-text-secondary font-mono mr-2">
                      #{m.match_number}
                    </span>
                  )}
                  {(m.team1 && m.team2) ? `${m.team1} vs ${m.team2}` : "—"}
                </div>
                <div className="text-[10px] text-text-secondary flex flex-wrap gap-x-2 gap-y-0.5">
                  <span>{m.match_date || "no date"}</span>
                  {m.format && <span>· {m.format}</span>}
                  {!competitionMode && m.competition && <span>· {m.competition}</span>}
                  {m.state && <span>· {m.state}</span>}
                  {m.venue && <span>· {m.venue}</span>}
                  {m.ball_count > 0 && (
                    <span>· {m.ball_count.toLocaleString("en-IN")} balls</span>
                  )}
                </div>
                {/* Any cross-user view (competition / format / both):
                    identify the coder so the founder doesn't have to
                    drill back into folders to figure out who scored
                    which match. */}
                {crossUserMode && m.owner?.email && (
                  <div className="text-[10px] text-text-secondary flex items-center gap-1 mt-0.5">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{m.owner.email}</span>
                  </div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                {/* Scorecard — innings totals, batting / bowling cards,
                    fall-of-wickets. Navigates in the same tab; the
                    folder + filter state is URL-backed so the browser
                    back button returns the operator here. */}
                <Link
                  to={`/cricket/matches/${m.id}/scorecard`}
                  title="Open scorecard"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25 inline-flex items-center gap-1"
                >
                  <ClipboardList className="w-3 h-3" /> Scorecard
                </Link>
                {/* QC — ball-by-ball quality-check view. Highlights
                    every saved delivery + the operator's tags so the
                    founder can audit before shipping the xlsx. */}
                <Link
                  to={`/ball/qc/${m.id}`}
                  title="Open ball-by-ball QC"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center gap-1"
                >
                  <ShieldCheck className="w-3 h-3" /> QC
                </Link>
                {/* Video Tagging — full-match MP4 → per-ball IN/OUT
                    stamps → server-side ffmpeg slicer. Same downstream
                    pipeline as the math + OCR aligners, but with
                    operator-authored timestamps. */}
                <Link
                  to={`/admin/matches/${m.id}/video-tagging`}
                  title="Upload full-match MP4 + manually tag clip IN/OUT per ball"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 inline-flex items-center gap-1"
                >
                  🎞 Tag
                </Link>
                {/* Retrieve — masters live in Glacier Deep Archive; the
                    operator clicks this ~2 days before a tagging session
                    so the video is playable when they sit down. Only
                    renders on matches that have an uploaded video. */}
                {videoStates[m.id]?.state === "archived" && (
                  <button
                    onClick={() => handleRetrieve(m.id)}
                    disabled={retrievingId === m.id}
                    title="Full-match video is in Glacier Deep Archive. Start the retrieval now — taggable within 48 hours, stays available 14 days."
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-50 inline-flex items-center gap-1"
                  >
                    {retrievingId === m.id
                      ? <><RefreshCw className="w-3 h-3 animate-spin" /> …</>
                      : <>⏳ Retrieve</>}
                  </button>
                )}
                {videoStates[m.id]?.state === "retrieving" && (
                  <span
                    title="Video retrieval in progress — taggable within 48 hours of the request."
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300/80 inline-flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3 animate-spin" /> Retrieving
                  </span>
                )}
                {(videoStates[m.id]?.state === "restored" ||
                  videoStates[m.id]?.state === "available") && (
                  <span
                    title={
                      videoStates[m.id]?.state === "restored"
                        ? `Video retrieved from archive — taggable${videoStates[m.id]?.restore_expiry ? ` until ${new Date(videoStates[m.id].restore_expiry).toLocaleDateString()}` : ""}, then it re-archives automatically.`
                        : "Full-match video is instantly available — taggable now."
                    }
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300/80 inline-flex items-center gap-1"
                  >
                    ✓ Video ready
                  </span>
                )}
                {/* Sync — remap archival Player FKs in this match to
                    canonical Players so runs/wickets accrue on real
                    profile stats. Same engine as tournament-scope sync,
                    scoped to one Match. */}
                <Link
                  to={`/cricket/matches/${m.id}/sync`}
                  title="Sync archival players to canonical profiles"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-rose-500/15 border border-rose-500/40 text-rose-300 hover:bg-rose-500/25 inline-flex items-center gap-1"
                >
                  <RefreshCcw className="w-3 h-3" /> Sync
                </Link>
                <button
                  onClick={() => handleDownload(m.id)}
                  disabled={downloadingId === m.id}
                  title="Download ball-by-ball as Excel"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-button-primary/15 border border-button-primary/40 text-button-primary hover:bg-button-primary/25 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {downloadingId === m.id
                    ? <><RefreshCw className="w-3 h-3 animate-spin" /> …</>
                    : <><FileSpreadsheet className="w-3 h-3" /> Excel</>}
                </button>
                {/* Delete — opens the confirm modal. Destructive action,
                    so red styling + extra step before the request fires. */}
                <button
                  onClick={() => setPendingDelete(m)}
                  disabled={deletingId === m.id}
                  title="Delete this match (cascades innings + balls + videos)"
                  className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {deletingId === m.id
                    ? <><RefreshCw className="w-3 h-3 animate-spin" /> …</>
                    : <><Trash2 className="w-3 h-3" /> Delete</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => deletingId == null && setPendingDelete(null)}
        >
          <div
            className="bg-bg-primary border border-border-default rounded-lg max-w-md w-full p-4 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-2 mb-3">
              <Trash2 className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-base font-semibold mb-1">Delete this match?</div>
                <div className="text-xs text-text-secondary">
                  {pendingDelete.match_number != null && (
                    <span className="font-mono mr-1">#{pendingDelete.match_number}</span>
                  )}
                  <span className="font-medium text-text-primary">
                    {pendingDelete.team1 || "—"} vs {pendingDelete.team2 || "—"}
                  </span>
                  {pendingDelete.match_date && <> on {pendingDelete.match_date}</>}
                  {pendingDelete.format && <> · {pendingDelete.format}</>}
                  {pendingDelete.ball_count > 0 && (
                    <> · {pendingDelete.ball_count.toLocaleString("en-IN")} balls</>
                  )}
                </div>
              </div>
              <button
                onClick={() => deletingId == null && setPendingDelete(null)}
                className="text-text-secondary hover:text-text-primary"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 mb-4">
              This permanently removes the match, every innings and ball,
              all wickets / analytics / videos linked to it. There is no undo.
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deletingId != null}
                className="px-3 py-1.5 text-xs rounded bg-bg-secondary text-text-primary hover:bg-bg-secondary/70 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deletingId != null}
                className="px-3 py-1.5 text-xs rounded bg-red-500/80 text-white hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-1"
              >
                {deletingId != null
                  ? <><RefreshCw className="w-3 h-3 animate-spin" /> Deleting…</>
                  : <><Trash2 className="w-3 h-3" /> Delete match</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && count > 20 && (
        <div className="mt-4 flex items-center justify-between text-xs">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded bg-bg-secondary text-text-primary disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-text-secondary">
            Page {page} of {Math.max(1, Math.ceil(count / 20))}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page * 20 >= count}
            className="px-3 py-1 rounded bg-bg-secondary text-text-primary disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </>
  );
}

// ── Page root: drives the folder vs. match-list view ─────────────────
export default function AdminMatches() {
  const [searchParams, setSearchParams] = useSearchParams();
  // owner is the currently-opened folder. URL-backed so a refresh
  // keeps the operator in the same drill-down.
  const [owner, setOwner] = useState(() => {
    const e = searchParams.get("owner_email");
    const id = searchParams.get("owner_id");
    const name = searchParams.get("owner_name");
    const lifecycle = searchParams.get("owner_lifecycle") || "";
    // Drill-in supports folders without an email too (legacy hard-delete
    // orphans). Use user_id as the route key in that case.
    if (e || id) {
      return {
        email: e || "",
        user_id: id,
        full_name: name || e || `user#${id || "?"}`,
        lifecycle,
      };
    }
    return null;
  });
  const [ownerSearch, setOwnerSearch] = useState(searchParams.get("q") || "");
  // Competition options shared by FolderList's "Browse by competition"
  // picker AND the inline filter inside MatchList (owner mode). Fetched
  // once at root so we don't pay a round-trip on every drill-in.
  const [competitionOptions, setCompetitionOptions] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/api/admin/v2/competitions/")
      .then((res) => {
        if (!alive) return;
        const data = res.data?.results || [];
        setCompetitionOptions(data);
      })
      .catch(() => { /* keep empty list */ });
    return () => { alive = false; };
  }, []);

  // Format options — fed from /api/matches/matchtypes/ so the picker
  // matches every active MatchType. Falls back to FORMATS_FALLBACK
  // until the request resolves.
  const [formatOptions, setFormatOptions] = useState(FORMATS_FALLBACK);
  useEffect(() => {
    let alive = true;
    api.get("/api/matches/matchtypes/")
      .then((res) => {
        if (!alive) return;
        const data = res.data?.results || res.data || [];
        if (!Array.isArray(data) || data.length === 0) return;
        setFormatOptions([
          { key: "", label: "All formats" },
          ...data.map((r) => ({
            key: (r.code || r.name || "").toUpperCase(),
            label: r.name || r.code,
          })),
        ]);
      })
      .catch(() => { /* keep fallback */ });
    return () => { alive = false; };
  }, []);

  // Cross-user competition-scope view. Hydrated from ?competition=<id>.
  // Only meaningful when `owner` is null (the two scopes are mutually
  // exclusive — owner-mode shows its own competition filter inside).
  const competitionScope = (() => {
    const cid = searchParams.get("competition");
    if (!cid || owner) return null;
    const c = competitionOptions.find((o) => o.id === cid);
    // Fall back to a stub so the view renders before the dropdown
    // options have loaded; the name gets filled in on next render.
    return c
      ? { id: c.id, name: c.name, season: c.season }
      : { id: cid, name: "Competition", season: "" };
  })();
  // Cross-user format-scope. Either alone or together with competition.
  const formatScope = (() => {
    if (owner) return "";
    return (searchParams.get("format") || "").toUpperCase();
  })();
  const inCrossUserView = !owner && (competitionScope || formatScope);

  // Reflect owner-search + competition-scope in URL so refresh holds
  // the current view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (owner) {
      if (owner.email) next.set("owner_email", owner.email);
      else next.delete("owner_email");
      if (owner.user_id != null) next.set("owner_id", String(owner.user_id));
      else next.delete("owner_id");
      if (owner.full_name) next.set("owner_name", owner.full_name);
      else next.delete("owner_name");
      if (owner.lifecycle) next.set("owner_lifecycle", owner.lifecycle);
      else next.delete("owner_lifecycle");
      next.delete("q");
      next.delete("competition");
      next.delete("format");
    } else {
      next.delete("owner_email");
      next.delete("owner_id");
      next.delete("owner_name");
      next.delete("owner_lifecycle");
      if (ownerSearch) next.set("q", ownerSearch);
      else next.delete("q");
      // competition + format are set/cleared by their own handlers
      // (pickCompetition / pickFormat / clearCrossUserScope) so we
      // don't touch them here.
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, ownerSearch]);

  const clearCrossUserScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("competition");
    next.delete("format");
    setSearchParams(next, { replace: true });
  };
  const pickCompetition = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("competition", id);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };
  const pickFormat = (code) => {
    const next = new URLSearchParams(searchParams);
    next.set("format", code);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-4">
      {owner ? (
        <MatchList
          owner={owner}
          competitionOptions={competitionOptions}
          onBack={() => setOwner(null)}
        />
      ) : inCrossUserView ? (
        <MatchList
          competition={competitionScope}
          initialFormat={formatScope}
          competitionOptions={competitionOptions}
          onBack={clearCrossUserScope}
        />
      ) : (
        <FolderList
          ownerSearch={ownerSearch}
          setOwnerSearch={setOwnerSearch}
          competitionOptions={competitionOptions}
          onPickCompetition={pickCompetition}
          formatOptions={formatOptions}
          onPickFormat={pickFormat}
          onOpen={(o) => setOwner(o)}
        />
      )}
    </div>
  );
}
